import React from 'react'
import { Box, Card, CardActionArea, Typography } from '@mui/material'
import { alpha, Theme } from '@mui/material/styles'
import EditNoteIcon from '@mui/icons-material/EditNote'
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolderOutlined'

export type ModeChoice = 'overwrite' | 'new'

const CHOICES: { key: ModeChoice; title: string; description: string; icon: React.ReactNode }[] = [
  {
    key: 'overwrite',
    title: 'Overwrite these tables',
    description: 'Keep the same names and replace the tables in this folder. This cannot be undone.',
    icon: <EditNoteIcon />,
  },
  {
    key: 'new',
    title: 'Create a new set',
    description: 'Keep these tables. Save new copies in a new folder, named with a prefix you choose.',
    icon: <CreateNewFolderIcon />,
  },
]

export interface StepModeProps {
  choice: ModeChoice | null
  onChoose: (choice: ModeChoice) => void
}

/** Step "Overwrite or new set": only for a folder in the user's own My Tables. */
const StepMode: React.FC<StepModeProps> = ({ choice, onChoose }) => (
  <Box>
    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
      You selected tables in your My Tables folder. Would you like to overwrite these tables, or create a new set in a different folder?
    </Typography>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      {CHOICES.map(c => {
        const selected = choice === c.key
        return (
          <Card
            key={c.key}
            variant="outlined"
            sx={{
              borderColor: selected ? 'info.main' : 'divider',
              borderWidth: selected ? 2 : 1,
              backgroundColor: selected ? (theme: Theme) => alpha(theme.palette.info.main, 0.05) : 'background.paper',
            }}
          >
            <CardActionArea
              onClick={() => onChoose(c.key)}
              aria-pressed={selected}
              sx={{ height: '100%', p: 3, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}
            >
              <Box sx={{ color: 'info.main' }}>{c.icon}</Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{c.title}</Typography>
              <Typography variant="body2" color="text.secondary">{c.description}</Typography>
            </CardActionArea>
          </Card>
        )
      })}
    </Box>
  </Box>
)

export default StepMode
